// frontend/src/pages/model/FormulaEditor.jsx
// The formula editor (Phase 3 step 13c-3): a dialog for one calculated column
// or measure. Left, the form: name, display name, the formula, description,
// format and the empty-value rule. Right, what can go in a formula: the
// dataset's fields and other calculations, and the function reference; clicking
// one inserts it at the caret. As you type, the server checks the formula
// (POST .../calculations/check, debounced) and the line under it says the
// result's type or what is wrong, with the offending part of the formula marked.
//
// Saving is the server's call: a 422 lands on the control it names, 409
// NAME_TAKEN on the name, and 409 CALCULATION_IN_USE (the change would break
// charts or other formulas) turns the footer into a confirmation that lists
// them and offers to save anyway (the request is sent again with force: true).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { AlertTriangle, Check, Loader2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { chartHref } from './modelLogic';
import {
  CHECK_DELAY_MS, FORMAT_LABELS, KIND_EXAMPLES, KIND_HELP, KIND_LABELS, MAX_DESCRIPTION, MAX_DISPLAY_NAME, MAX_NAME, MAX_SOURCE,
  consequenceText, draftFrom, draftProblems, emptyCalc, errorRange, errorTarget, functionInsert, groupFunctions, insertAt, inUseTitleFor,
  referenceText, reservedNames, suggestName, toCheckBody, toCreateBody, toPatchBody, typeText, usableCalculations, usableFields,
} from './calcLogic';

const field = 'h-9 w-full rounded-xl bg-[var(--ui-inset)] px-3 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-60';
const labelCls = 'mb-1 block text-[12px] text-[var(--ui-text-muted)]';
const muted = 'text-[var(--ui-text-muted)]';

function Problem({ id, text }) {
  if (!text) return null;
  return <p id={id} role="alert" className="mt-1 text-[12px] text-red-300">{text}</p>;
}

/** The formula with the error's range marked, under the textarea. */
export function ErrorMark({ source, pos }) {
  const { before, bad, after } = errorRange(source, pos);
  return (
    <pre className="mt-1.5 max-h-20 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-[var(--ui-inset)] px-2.5 py-1.5 font-mono text-[12px] text-[var(--ui-text-muted)] scrollbar-thin" aria-hidden="true">
      {before}<mark className="rounded-sm bg-red-500/30 text-red-200">{bad || ' '}</mark>{after}
    </pre>
  );
}

/** The live check's line: checking, the type, or the problem. */
export function CheckLine({ check, source }) {
  if (!check || check.status === 'idle') return <p className={`mt-1.5 text-[12px] ${muted}`}>The formula is checked as you type.</p>;
  if (check.status === 'checking') {
    return <p className={`mt-1.5 flex items-center gap-1.5 text-[12px] ${muted}`} role="status"><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Checking</p>;
  }
  if (check.status === 'ok') {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-emerald-400" role="status">
        <Check className="h-3.5 w-3.5" aria-hidden="true" /> Gives {typeText(check.data_type).toLowerCase()}
        {check.fields && check.fields.length > 0 && <span className={muted}>· uses {check.fields.concat(check.uses || []).length} field{check.fields.concat(check.uses || []).length === 1 ? '' : 's'}</span>}
      </p>
    );
  }
  return (
    <>
      <p className="mt-1.5 flex items-start gap-1.5 text-[12px] text-red-300" role="alert">
        <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" /> {check.error.message}
      </p>
      {check.error.pos && <ErrorMark source={source} pos={check.error.pos} />}
    </>
  );
}

/** One side of the reference: a searchable list of things to insert. */
function InsertList({ title, items, render, onPick, emptyText }) {
  return (
    <div>
      <p className={`mb-1 mt-3 text-[11px] uppercase tracking-wide ${muted}`}>{title}</p>
      {items.length === 0 && <p className={`text-[12px] ${muted}`}>{emptyText}</p>}
      <ul className="flex flex-col gap-px">
        {items.map((it) => (
          <li key={it.key || it.name}>
            <button type="button" onClick={() => onPick(it)} title={it.help || it.description || undefined}
              className="flex w-full items-baseline gap-2 rounded-lg px-2 py-1 text-left text-[12.5px] text-[var(--ui-text)] hover:bg-[var(--ui-inset-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
              {render(it)}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The editor's body, without state (rendered on its own in tests, inside a Dialog.Root).
 * draft: the form's values. problems: { [control]: text }. check: the live check's state.
 * inUse: null, or { charts, calculations, action } when the save was refused as breaking things.
 */
export function EditorForm({
  mode, draft, current, problems, check, saving, formError, inUse, fields, calculations, functions, reserved, tab, query,
  onDraft, onTab, onQuery, onInsert, onSave, onForce, onCancel, sourceRef,
}) {
  const isNew = mode === 'new';
  const kind = draft.kind;
  const fieldItems = useMemo(() => usableFields(fields, query), [fields, query]);
  const calcItems = useMemo(() => usableCalculations(calculations, kind, current ? current.key : null, query), [calculations, kind, current, query]);
  const fnGroups = useMemo(() => groupFunctions(functions, query, { kind }), [functions, query, kind]);
  const set = (k) => (e) => onDraft({ ...draft, [k]: e.target.value });

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Dialog.Title className="text-lg font-semibold tracking-[-0.01em]">
            {isNew ? `New ${KIND_LABELS[kind].toLowerCase()}` : `Edit ${current.display_name}`}
          </Dialog.Title>
          <Dialog.Description className={`mt-1 text-[12.5px] ${muted}`}>{KIND_HELP[kind]}</Dialog.Description>
        </div>
        <Dialog.Close asChild>
          <button type="button" aria-label="Close" className={`rounded-lg p-1 ${muted} hover:bg-[var(--ui-inset-hover)] hover:text-[var(--ui-text)]`}><X className="h-4 w-4" aria-hidden="true" /></button>
        </Dialog.Close>
      </div>

      <div className="grid gap-5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <form className="flex min-w-0 flex-col gap-3.5" onSubmit={(e) => { e.preventDefault(); onSave(); }} noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="calc-display-name" className={labelCls}>Display name</label>
              <input id="calc-display-name" type="text" value={draft.display_name} maxLength={MAX_DISPLAY_NAME} className={field}
                placeholder={isNew ? 'Monthly pay' : ''} disabled={saving} autoComplete="off" spellCheck={false} autoFocus={isNew}
                aria-invalid={Boolean(problems.display_name) || undefined} aria-describedby={problems.display_name ? 'calc-display-name-problem' : undefined}
                onChange={(e) => {
                  const next = { ...draft, display_name: e.target.value };
                  // A new formula's name follows its display name until the name is typed by hand.
                  if (isNew && (draft.name === '' || draft.name === suggestName(draft.display_name))) next.name = suggestName(e.target.value);
                  onDraft(next);
                }} />
              <Problem id="calc-display-name-problem" text={problems.display_name} />
            </div>
            <div>
              <label htmlFor="calc-name" className={labelCls}>Name in formulas</label>
              <input id="calc-name" type="text" value={draft.name} maxLength={MAX_NAME} className={`${field} font-mono`} disabled={saving || !isNew}
                placeholder="monthly_pay" autoComplete="off" spellCheck={false}
                title={isNew ? 'Lowercase letters, digits and _; it cannot change later' : 'A name never changes once saved (charts refer to it)'}
                aria-invalid={Boolean(problems.name) || undefined} aria-describedby={problems.name ? 'calc-name-problem' : 'calc-name-hint'}
                onChange={set('name')} />
              {problems.name ? <Problem id="calc-name-problem" text={problems.name} />
                : <p id="calc-name-hint" className={`mt-1 text-[11.5px] ${muted}`}>{isNew ? 'Cannot be changed later.' : 'Fixed once saved.'}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="calc-source" className={labelCls}>Formula</label>
            <textarea id="calc-source" ref={sourceRef} value={draft.source} rows={4} maxLength={MAX_SOURCE} disabled={saving}
              spellCheck={false} autoComplete="off" autoCapitalize="off" autoCorrect="off" autoFocus={!isNew}
              placeholder={KIND_EXAMPLES[kind][0]}
              aria-invalid={Boolean(problems.source) || (check && check.status === 'bad') || undefined}
              aria-describedby="calc-source-check"
              className="w-full resize-y rounded-xl bg-[var(--ui-inset)] px-3 py-2 font-mono text-[13px] leading-5 text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 disabled:opacity-60"
              onChange={set('source')} />
            <div id="calc-source-check">
              {problems.source ? <Problem text={problems.source} /> : <CheckLine check={check} source={draft.source} />}
            </div>
            <p className={`mt-1.5 text-[11.5px] ${muted}`}>
              Examples: {KIND_EXAMPLES[kind].map((ex, i) => (
                <React.Fragment key={ex}>{i > 0 && ' · '}<button type="button" onClick={() => onDraft({ ...draft, source: ex })} className="font-mono underline-offset-2 hover:text-[var(--ui-text)] hover:underline">{ex}</button></React.Fragment>
              ))}
            </p>
          </div>

          <div>
            <label htmlFor="calc-description" className={labelCls}>Description <span className="opacity-70">(optional)</span></label>
            <input id="calc-description" type="text" value={draft.description} maxLength={MAX_DESCRIPTION} className={field} disabled={saving}
              placeholder="What it means and when to use it" autoComplete="off" onChange={set('description')} />
            <Problem text={problems.description} />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="calc-format" className={labelCls}>Shown as</label>
              <select id="calc-format" value={draft.format || ''} disabled={saving} className={`${field} appearance-none [color-scheme:dark]`}
                onChange={(e) => onDraft({ ...draft, format: e.target.value || null })}>
                <option value="">Automatic</option>
                {Object.entries(FORMAT_LABELS).map(([v, text]) => <option key={v} value={v}>{text}</option>)}
              </select>
              <Problem text={problems.format} />
              {kind === 'column' && <p className={`mt-1 text-[11.5px] ${muted}`}>Only for a formula that gives a number.</p>}
            </div>
            <div className="flex items-end pb-1">
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[var(--ui-text)]">
                <input type="checkbox" checked={draft.null_policy === 'zero'} disabled={saving} className="h-4 w-4 rounded accent-[var(--ui-selected)]"
                  onChange={(e) => onDraft({ ...draft, null_policy: e.target.checked ? 'zero' : 'null' })} />
                Show 0 instead of blank
              </label>
            </div>
          </div>

          {formError && <p role="alert" className="rounded-lg bg-red-500/10 px-3 py-2 text-[12.5px] text-red-300">{formError}</p>}

          {inUse ? (
            <div className="rounded-xl bg-amber-500/10 p-3.5" role="alertdialog" aria-labelledby="calc-inuse-title">
              <p id="calc-inuse-title" className="flex items-center gap-2 text-[13.5px] font-medium text-[var(--ui-text)]">
                <AlertTriangle className="h-4 w-4 text-amber-500" aria-hidden="true" />
                {inUseTitleFor(current ? current.display_name : 'This formula', inUse.charts.length, inUse.calculations.length)}
              </p>
              <p className={`mt-1 text-[12.5px] ${muted}`}>{consequenceText(inUse.action)}</p>
              <ul className="mt-2 flex max-h-32 flex-col gap-0.5 overflow-y-auto text-[12.5px] scrollbar-thin">
                {inUse.charts.map((c) => {
                  const href = chartHref(c.id);
                  return <li key={`c${c.id}`} className="truncate">Chart: {href ? <a href={href} target="_blank" rel="noopener noreferrer" className="underline-offset-2 hover:underline">{c.title || `Chart ${c.id}`}</a> : (c.title || `Chart ${c.id}`)}</li>;
                })}
                {inUse.calculations.map((c) => <li key={c.key} className="truncate">{KIND_LABELS[c.kind]}: {c.display_name}</li>)}
              </ul>
              <div className="mt-3 flex items-center justify-end gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
                <Button type="button" variant="danger" size="sm" onClick={onForce} disabled={saving}>
                  {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />} Save anyway
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-1 flex items-center justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving || (check && check.status === 'checking')}>
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {isNew ? 'Create' : 'Save changes'}
              </Button>
            </div>
          )}
        </form>

        <aside className="min-w-0 rounded-2xl bg-[var(--ui-inset)]/60 p-3 ring-1 ring-white/5" aria-label="What a formula can use">
          <div role="tablist" aria-label="Insert" className="inline-flex rounded-xl bg-[var(--ui-inset)] p-0.5">
            {[['fields', 'Fields'], ['functions', 'Functions']].map(([id, text]) => (
              <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => onTab(id)}
                className={`rounded-[10px] px-3 py-1 text-[12.5px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 ${
                  tab === id ? 'bg-[var(--ui-selected)] text-[var(--ui-selected-text)]' : `${muted} hover:text-[var(--ui-text)]`}`}>
                {text}
              </button>
            ))}
          </div>
          <div className="relative mt-2.5">
            <Search className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${muted}`} aria-hidden="true" />
            <input type="search" value={query} onChange={(e) => onQuery(e.target.value)} placeholder={tab === 'fields' ? 'Search fields' : 'Search functions'}
              aria-label={tab === 'fields' ? 'Search fields' : 'Search functions'}
              className="h-8 w-full rounded-lg bg-[var(--ui-inset)] pl-8 pr-2 text-[12.5px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30" />
          </div>
          <div className="max-h-[360px] overflow-y-auto pr-1 scrollbar-thin">
            {tab === 'fields' ? (
              <>
                <InsertList title="Fields" items={fieldItems} emptyText="No fields match." onPick={(f) => onInsert(referenceText(f))}
                  render={(f) => (<><span className="truncate">{f.display_name}</span><span className={`ml-auto shrink-0 text-[11px] ${muted}`}>{typeText(f.data_type)}</span></>)} />
                <InsertList title={kind === 'measure' ? 'Calculations' : 'Calculated columns'} items={calcItems} onPick={(c) => onInsert(referenceText(c))}
                  emptyText={kind === 'measure' ? 'None yet.' : 'None yet. A column cannot use a measure.'}
                  render={(c) => (<><span className="truncate">{c.display_name}</span><span className={`ml-auto shrink-0 text-[11px] ${muted}`}>{c.kind === 'measure' ? 'Measure' : typeText(c.data_type)}</span></>)} />
              </>
            ) : fnGroups.map((g) => (
              <InsertList key={g.label} title={g.label} items={g.items} emptyText="" onPick={(fn) => { const ins = functionInsert(fn); onInsert(ins.text, ins.caret); }}
                render={(fn) => (<><span className="shrink-0 font-mono text-[12px]">{fn.signature}</span><span className={`truncate text-[11.5px] ${muted}`}>{fn.help}</span></>)} />
            ))}
            {tab === 'functions' && fnGroups.length === 0 && <p className={`mt-3 text-[12px] ${muted}`}>No functions match.</p>}
          </div>
        </aside>
      </div>
    </>
  );
}

/**
 * The dialog with its state. request: null (closed) or { mode: 'new' | 'edit', kind, current }.
 * check(body) -> the server's result; save(body, { key, force }) -> the saved calculation (throws SemanticError).
 */
export default function FormulaEditor({ request, fields, calculations, functions, check, save, onSaved, onCancel }) {
  const open = Boolean(request);
  const current = request && request.mode === 'edit' ? request.current : null;
  const [draft, setDraft] = useState(null);
  const [problems, setProblems] = useState({});
  const [formError, setFormError] = useState('');
  const [inUse, setInUse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [live, setLive] = useState({ status: 'idle' });
  const [tab, setTab] = useState('fields');
  const [query, setQuery] = useState('');
  const sourceRef = useRef(null);
  const checkSeq = useRef(0);
  const reserved = useMemo(() => reservedNames(functions), [functions]);

  useEffect(() => {
    if (!request) { setDraft(null); return; }
    setDraft(request.mode === 'edit' ? draftFrom(request.current) : emptyCalc(request.kind));
    setProblems({}); setFormError(''); setInUse(null); setSaving(false); setLive({ status: 'idle' }); setTab('fields'); setQuery('');
  }, [request]);

  // The live check, a moment after the last change to the formula.
  const source = draft ? draft.source : '';
  const kind = draft ? draft.kind : '';
  const name = draft ? draft.name : '';
  useEffect(() => {
    if (!draft || !source.trim()) { setLive({ status: 'idle' }); return undefined; }
    const seq = ++checkSeq.current;
    const ctrl = new AbortController();
    setLive((l) => (l.status === 'idle' ? { status: 'checking' } : { ...l, status: 'checking' }));
    const t = setTimeout(() => {
      check(toCheckBody({ kind, source, name }, current ? current.key : null), { signal: ctrl.signal })
        .then((res) => {
          if (seq !== checkSeq.current) return;
          if (res && res.ok) setLive({ status: 'ok', data_type: res.data_type, fields: res.fields || [], uses: res.uses || [] });
          else if (res && res.error && errorTarget(res.error) === 'source') setLive({ status: 'bad', error: res.error });
          else setLive({ status: 'idle' });     // a name problem is shown by the name control itself
        })
        .catch(() => { if (seq === checkSeq.current && !ctrl.signal.aborted) setLive({ status: 'idle' }); });
    }, CHECK_DELAY_MS);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [source, kind, name, current && current.key]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onDraft = (next) => { setDraft(next); setInUse(null); setFormError(''); setProblems((p) => (Object.keys(p).length ? {} : p)); };

  const onInsert = (text, caretOffset) => {
    const el = sourceRef.current;
    const start = el ? el.selectionStart : source.length;
    const end = el ? el.selectionEnd : source.length;
    const r = insertAt(source, start, end, text, caretOffset);
    onDraft({ ...draft, source: r.source });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(r.caret, r.caret); } });
  };

  async function submit(force = false) {
    if (!draft || saving) return;
    const isNew = !current;
    const found = draftProblems(draft, reserved, { isNew });
    if (Object.keys(found).length) { setProblems(found); return; }
    const body = isNew ? toCreateBody(draft) : toPatchBody(draft, current);
    if (!isNew && !body) { onCancel(); return; }          // nothing changed
    setSaving(true); setFormError(''); setProblems({});
    try {
      const saved = await save(force ? { ...body, force: true } : body, { key: current ? current.key : null });
      setSaving(false);
      onSaved(saved, isNew ? 'created' : 'updated');
    } catch (err) {
      setSaving(false);
      if (err && err.name === 'SemanticError') {
        if (err.code === 'CALCULATION_IN_USE') { setInUse({ charts: err.charts, calculations: err.calculations, action: body.is_enabled === false ? 'disable' : 'change' }); return; }
        const target = errorTarget(err);
        if (target === 'source' && err.pos) setLive({ status: 'bad', error: { message: err.message, pos: err.pos } });
        if (target !== 'form') setProblems({ [target]: err.message }); else setFormError(err.message);
        return;
      }
      setFormError("Couldn't reach the server. Check your connection and try again.");
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !saving) onCancel(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}      // the form's autoFocus picks the control: the display name of a new formula, the formula of an edit
          className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-4xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none scrollbar-thin"
        >
          {draft && (
            <EditorForm
              mode={current ? 'edit' : 'new'} draft={draft} current={current} problems={problems} check={live} saving={saving}
              formError={formError} inUse={inUse} fields={fields} calculations={calculations} functions={functions} reserved={reserved}
              tab={tab} query={query} onDraft={onDraft} onTab={(t) => { setTab(t); setQuery(''); }} onQuery={setQuery} onInsert={onInsert}
              onSave={() => submit(false)} onForce={() => submit(true)} onCancel={onCancel} sourceRef={sourceRef}
            />
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
